import { useEffect, useState } from 'react';

/**
 * Returns a value that only updates after `delayMs` of inactivity.
 *
 * Useful for keystroke-driven filter inputs that trigger expensive
 * downstream work (search ranking, URL navigation, list re-render).
 *
 * The returned value updates after the debounce window, so the consumer
 * can safely pass it to memoized filters or `router.push` without
 * thrashing the URL on every keystroke.
 *
 * @template T
 * @param {T} value The live value to debounce.
 * @param {number} delayMs Debounce window in milliseconds. Defaults to 300.
 * @returns {T} The debounced value, which lags `value` by `delayMs`.
 */
export default function useDebouncedValue(value, delayMs = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    if (delayMs <= 0) {
      setDebouncedValue(value);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, delayMs]);

  return debouncedValue;
}
